import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Divider,
    Menu,
    MenuItem,
    InputBase,
    Avatar,
    CircularProgress,
    Typography
} from '@mui/material';
import TeamLeadDetailMenu from './TeamLeadDetailMenu';
import TeamLeadDetailCard from './TeamLeadDetailCard';
import TeamLeadDetailsEdit from './editLeadDetails/TeamLeadDetailsEdit';
import { EditOutlinedIcon, SaveAltOutlinedIcon, EmailOutlinedIcon } from 'icons/materialIcons';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import TeamSendLeadEmailModal from './TeamSendLeadEmailModal';
import { useNavigate } from 'react-router-dom';
import { downloadVCard } from 'utils/vcfUtils';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import { useLeadCaptureContext } from 'contexts/LeadCaptureContext';
import ExportUtils from 'utils/contacts/ExportUtils';
import { updateLead } from 'apis/leadCaptureApi';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import { useTeam } from 'team/contexts/teamContext';
import { useAuth } from 'contexts/AuthContext';
import avatarPlaceholder from 'images/avatar-cropped.png';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamLeadDetailsContainer = ({ lead, onBack, onDeleteComplete, startInEditMode = false }) => {
    const [isEditing, setIsEditing] = useState(startInEditMode);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [currentLead, setCurrentLead] = useState(lead);
    const navigate = useNavigate();
    const { updateContact } = useLeadCaptureContext();
    const { currentTeamId } = useTeam();
    const { authState } = useAuth();

    // Reassign state
    const [reassignMenuOpen, setReassignMenuOpen] = useState(false);
    const [teamMemberCards, setTeamMemberCards] = useState([]);
    const [cardSearchQuery, setCardSearchQuery] = useState('');
    const [loadingCards, setLoadingCards] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const reassignButtonRef = useRef(null);
    const cardListRef = useRef(null);

    const handleUpdateLead = (updatedLead) => {
        // Update the current lead state with the new details
        setCurrentLead(updatedLead);

        // Update the lead in the global context
        updateContact(updatedLead);

        // Exit edit mode
        setIsEditing(false);
    };

    // Function to handle edit click from any source
    const handleEditClick = () => {
        setIsEditing(true);
    };

    // Function to handle successful deletion
    const handleDeleteComplete = () => {
        // Navigate back to the leads list with a flag indicating deletion happened
        if (onDeleteComplete) {
            onDeleteComplete();
        } else {
            onBack();
        }
    };

    // Function to handle email click
    const handleEmailClick = () => {
        setIsEmailModalOpen(true);
    };

    // Function to handle CSV export
    const handleExportToCSV = () => {
        if (currentLead) {
            try {
                ExportUtils.exportSingleLead(currentLead);
            } catch (error) {
                console.error('Error exporting lead to CSV:', error);
            }
        }
    };

    // Function to handle Excel export
    const handleExportToExcel = () => {
        if (currentLead) {
            try {
                ExportUtils.exportSingleLeadToExcel(currentLead);
            } catch (error) {
                console.error('Error exporting lead to Excel:', error);
            }
        }
    };

    // Function to prepare lead data for VCF download
    const prepareLeadData = () => {
        return {
            contact_info: {
                fullName: currentLead.leadName,
                email: currentLead.leadEmail,
                phoneNumber: currentLead.leadPhoneNumber,
                company: currentLead.leadCompany,
                jobTitle: currentLead.leadJobTitle,
                location: currentLead.leadAddress
            },
            bio: currentLead.leadNotes,
            profilePictureUrl: currentLead.profilePictureUrl
        };
    };

    // Fetch team member cards for reassign
    useEffect(() => {
        const fetchTeamMemberCards = async () => {
            if (!currentTeamId || !reassignMenuOpen) return;

            try {
                setLoadingCards(true);
                const response = await getTeamMembers(currentTeamId, { status: 'active' });
                const members = response?.members || [];

                const allCards = [];
                members.forEach(member => {
                    if (member.defaultCard && member.defaultCard._id) {
                        const cardWithUserId = {
                            ...member.defaultCard,
                            userId: member.userId || member.user?.userId
                        };
                        allCards.push(cardWithUserId);
                    }
                });

                setTeamMemberCards(allCards);
            } catch (error) {
                console.error('Failed to fetch team member cards:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load team member cards',
                    severity: 'error'
                });
            } finally {
                setLoadingCards(false);
            }
        };

        if (reassignMenuOpen && currentTeamId) {
            fetchTeamMemberCards();
        }
    }, [reassignMenuOpen, currentTeamId]);

    // Filter cards based on search
    const filteredCards = teamMemberCards.filter(card => {
        if (!cardSearchQuery) return true;
        const query = cardSearchQuery.toLowerCase();
        const fullName = (card.contact_info?.fullName || card.cardName || '').toLowerCase();
        const email = (card.contact_info?.email || '').toLowerCase();
        const jobTitle = (card.contact_info?.jobTitle || '').toLowerCase();
        const company = (card.contact_info?.company || '').toLowerCase();
        return fullName.includes(query) || email.includes(query) ||
            jobTitle.includes(query) || company.includes(query);
    });

    // Handle reassign
    const handleReassign = async (cardId) => {
        if (!currentLead?._id || !cardId) return;

        try {
            setLoadingCards(true);
            // Include leadEmail (required by validation) along with cardId update
            const updatedLead = await updateLead(currentLead._id, {
                cardId: cardId,
                leadEmail: currentLead.leadEmail // Required for validation
            });

            if (updatedLead && updatedLead.lead) {
                // Update currentLead with the new cardId
                const updatedLeadData = {
                    ...currentLead,
                    cardId: updatedLead.lead.cardId
                };
                setCurrentLead(updatedLeadData);
                updateContact(updatedLeadData);
                setReassignMenuOpen(false);
                setCardSearchQuery('');
                setSnackbar({
                    open: true,
                    message: 'Lead reassigned successfully',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Failed to reassign lead:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to reassign lead',
                severity: 'error'
            });
        } finally {
            setLoadingCards(false);
        }
    };

    const handleReassignClick = () => {
        setReassignMenuOpen(true);
        setCardSearchQuery('');
    };

    const actionButtons = [
        {
            icon: EditOutlinedIcon,
            text: "Edit",
            onClick: handleEditClick
        },
        {
            icon: SaveAltOutlinedIcon,
            text: "Save as contact",
            onClick: () => {
                // Prepare lead data for VCF download
                const leadData = prepareLeadData();
                downloadVCard(leadData);
            }
        },
        {
            icon: EmailOutlinedIcon,
            text: "Email Lead",
            onClick: handleEmailClick
        },
        {
            icon: LoopOutlinedIcon,
            text: "Reassign",
            onClick: handleReassignClick,
            buttonRef: reassignButtonRef
        }
    ];

    return (
        <Box sx={{
            width: '100%',
            height: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <TeamLeadDetailMenu
                lead={currentLead}
                onBack={onBack}
                onEditClick={handleEditClick}
                onDeleteComplete={handleDeleteComplete}
                onReassignClick={handleReassignClick}
                reassignButtonRef={reassignButtonRef}
            />

            <Box sx={{
                bgcolor: '#ffffff',
                borderRadius: 4,
                m: 2,
                p: 0,
                display: 'flex',
                flex: 1,
                overflow: 'auto'
            }}>
                <Box sx={{
                    width: '100%',
                    p: 6,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {!isEditing && (
                        <Box sx={{
                            display: 'flex',
                            gap: 3,
                            mb: 2,
                            flexShrink: 0
                        }}>
                            {actionButtons.map((button, index) => (
                                <Box key={index} ref={button.buttonRef}>
                                    <WhiteButtonWithIcon
                                        icon={button.icon}
                                        text={button.text}
                                        onClick={button.onClick || (() => console.log(`${button.text} clicked`))}
                                        padding="8px 48px"
                                        data-edit-lead-button={button.text === "Edit" ? true : undefined}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {isEditing ? (
                            <TeamLeadDetailsEdit
                                lead={currentLead}
                                onCancel={() => setIsEditing(false)}
                                onUpdate={handleUpdateLead}
                            />
                        ) : (
                            <TeamLeadDetailCard lead={currentLead} />
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Add the email modal */}
            <TeamSendLeadEmailModal
                open={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                contact={currentLead}
            />

            {/* Reassign Menu */}
            {reassignButtonRef.current && (
                <Menu
                    anchorEl={reassignButtonRef.current}
                    open={reassignMenuOpen}
                    onClose={() => {
                        setReassignMenuOpen(false);
                        setCardSearchQuery('');
                    }}
                    disableAutoFocusItem
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                            width: {
                                xs: '70vw',
                                sm: '320px',
                                md: '350px'
                            },
                            maxWidth: '350px',
                            maxHeight: '80vh',
                            overflow: 'hidden',
                            marginTop: '12px',
                            padding: '12px',
                            backgroundColor: '#fafafa'
                        }
                    }}
                    MenuListProps={{
                        sx: {
                            padding: 0
                        }
                    }}
                    slotProps={{
                        backdrop: {
                            sx: {
                                cursor: 'default'
                            }
                        }
                    }}
                >
                    {/* Search Bar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            border: '1px solid #e3e3e3',
                            padding: '8px 16px',
                            marginBottom: '12px',
                            '&:hover': {
                                boxShadow: 2,
                            },
                        }}
                    >
                        <InputBase
                            fullWidth
                            value={cardSearchQuery}
                            onChange={(e) => setCardSearchQuery(e.target.value)}
                            placeholder="Search..."
                            sx={{
                                fontSize: '0.7rem',
                                color: '#4f4f4f',
                                '& .MuiInputBase-input::placeholder': {
                                    color: '#828282',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>

                    {/* Cards list */}
                    <Box
                        ref={cardListRef}
                        sx={{
                            maxHeight: '60vh',
                            overflowY: 'auto'
                        }}
                    >
                        {loadingCards ? (
                            <Box sx={{ padding: 2, textAlign: 'center' }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : filteredCards.length === 0 ? (
                            <Box sx={{ padding: 2, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.875rem', color: '#828282' }}>
                                    {cardSearchQuery ? 'No team members found' : 'No team members available'}
                                </Typography>
                            </Box>
                        ) : (
                            filteredCards.map((card) => {
                                const isSelected = currentLead?.cardId === card._id;
                                return (
                                    <MenuItem
                                        key={card._id}
                                        data-card-id={card._id}
                                        onClick={() => handleReassign(card._id)}
                                        selected={isSelected}
                                        sx={{
                                            borderRadius: '8px',
                                            marginBottom: '4px',
                                            '&.Mui-selected': {
                                                backgroundColor: '#f0f0f0',
                                                '&:hover': {
                                                    backgroundColor: '#e0e0e0'
                                                }
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                            <Avatar
                                                src={card.profilePictureUrl || avatarPlaceholder}
                                                alt={card.contact_info?.fullName || card.cardName}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>
                                                    {card.contact_info?.fullName || card.cardName || 'Unnamed Card'}
                                                </Typography>
                                                {(card.contact_info?.jobTitle || card.contact_info?.company) && (
                                                    <Typography sx={{ fontSize: '0.8rem', color: '#828282', marginTop: '2px' }}>
                                                        {[card.contact_info?.jobTitle, card.contact_info?.company].filter(Boolean).join(' • ')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                );
                            })
                        )}
                    </Box>
                </Menu>
            )}

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </Box>
    );
};

export default TeamLeadDetailsContainer;